const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
const SCOPE_SELECTOR = '.product-info';

function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
  if (!Number.isFinite(cents)) return '0';

  const [units, subunits] = (cents / 100).toFixed(precision).split('.');
  const groupedUnits = units.replace(/(\d)(?=(\d\d\d)+$)/g, `$1${thousands}`);

  return subunits ? `${groupedUnits}${decimal}${subunits}` : groupedUnits;
}

function formatMoney(cents) {
  const settings = window.themeVariables?.settings ?? {};
  const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) || '${{amount}}';
  const placeholder = format.match(MONEY_PLACEHOLDER);

  if (!placeholder) return format;

  let value;

  switch (placeholder[1]) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return format.replace(MONEY_PLACEHOLDER, () => value);
}

class VolumePricing extends HTMLElement {
  #abortController;
  #tiers;

  connectedCallback() {
    this.#abortController = new AbortController();

    const scope = this.#scope;

    scope.addEventListener('input', this.#onQuantityChange, { signal: this.#abortController.signal });
    scope.addEventListener('change', this.#onQuantityChange, { signal: this.#abortController.signal });

    this.update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get tiers() {
    if (!this.#tiers) {
      let parsed = [];

      try {
        parsed = JSON.parse(this.getAttribute('data-tiers') ?? '[]');
      } catch {
        parsed = [];
      }

      this.#tiers = parsed
        .map(([minimumQuantity, price]) => ({ minimumQuantity: Number(minimumQuantity), price: Number(price) }))
        .filter(({ minimumQuantity, price }) => Number.isFinite(minimumQuantity) && Number.isFinite(price))
        .sort((a, b) => a.minimumQuantity - b.minimumQuantity);
    }

    return this.#tiers;
  }

  update() {
    if (!this.isConnected || this.tiers.length === 0) return;

    const scope = this.#scope;
    const unavailable = this.hasAttribute('data-unavailable');
    const quantity = Math.max(parseInt(scope.querySelector('input[name="quantity"]')?.value ?? '', 10) || 0, 0);
    const tier = this.#tierFor(quantity);

    scope.querySelectorAll('[data-volume-unit-price]').forEach((element) => {
      element.textContent = formatMoney(tier.price);
    });

    scope.querySelectorAll('[data-volume-total]').forEach((element) => {
      element.textContent = formatMoney(unavailable ? 0 : tier.price * quantity);
    });
  }

  get #scope() {
    return this.closest(SCOPE_SELECTOR) ?? document;
  }

  #onQuantityChange = (event) => {
    if (event.target.matches?.('input[name="quantity"]')) {
      this.update();
    }
  };

  #tierFor(quantity) {
    let activeTier = this.tiers[0];

    for (const tier of this.tiers) {
      if (quantity < tier.minimumQuantity) break;
      activeTier = tier;
    }

    return activeTier;
  }
}

if (!window.customElements.get('volume-pricing')) {
  window.customElements.define('volume-pricing', VolumePricing);
}
